<template>
  <div
    class="profile-history"
    :class="{ 'profile-history--legacy': useLegacySiloProfileVisualization }"
  >
    <div v-if="loading && !hasError" class="profile-history-loading">
      <Loader style="margin: 80px auto;" />
    </div>

    <div class="container-warnig" v-else-if="hasError && !loading">
      <div class="warning">
        <div class="text">
          <div style="text-align: center">{{ errorMessage }}. Sem dados para o período solicitado. Por favor, verifique se o sensor não está soterrado e cheque se o sensor e o painel estão energizados e com conexão à internet. Caso não haja nenhum desses problemas, favor entrar em contato com o suporte PecSmart.</div>
        </div>
      </div>
    </div>

    <div
      v-if="!loading && !hasError && firstProfile"
      class="profile-panel"
      :class="{ 'profile-panel--legacy': useLegacySiloProfileVisualization }"
    >
      <header class="profile-panel-header">
        <h2 class="profile-panel-title">{{ sensorName }}</h2>
        <p
          v-if="loadingChart || loadingCurvesForCanvas"
          class="profile-panel-hint loading-text"
        >
          <template v-if="loadingChart">Carregando histórico de perfis...</template>
          <template v-else>Atualizando curvas do silo...</template>
        </p>
      </header>

      <div
        class="profile-panel-visual"
        :class="{ 'profile-panel-visual--legacy': useLegacySiloProfileVisualization }"
      >
        <aside
          v-if="!useLegacySiloProfileVisualization || hasCanvasSeries"
          class="profile-panel-silo"
          :class="{
            'profile-panel-silo--legacy': useLegacySiloProfileVisualization,
            'canvas-pulsing': loadingCurvesForCanvas,
          }"
        >
          <SiloProfileVisualizationCanvas
            v-if="useLegacySiloProfileVisualization && hasCanvasSeries"
            class="silo-canvas-responsive"
            :siloSeries="siloSeries"
            :profileSeries="profileSeries"
            :fillGrainArea="true"
            :verifiedProfileSeries="profileSeries"
            :straightFill="useStraightSiloProfileVisualization"
            :level="canvasFillLevel"
          />
          <SiloCard
            v-else-if="!useLegacySiloProfileVisualization && transformedSiloData"
            display="visualization"
            variant="detail"
            hide-header
            :silo="transformedSiloData"
            :plate-height="plateHeight"
            :farm-pk="farmPk"
          />
        </aside>

        <section
          class="profile-panel-chart"
          :class="{ 'profile-panel-chart--legacy': useLegacySiloProfileVisualization }"
        >
          <h3
            v-if="useLegacySiloProfileVisualization"
            class="profile-panel-chart-title"
          >
            Historico de nivel
          </h3>
          <SiloProfileHistoryChart
            class="chart-responsive"
            :class="{ 'chart-responsive--legacy': useLegacySiloProfileVisualization }"
            :silo_profiles="mappedProfiles"
            :start-date="chartStartDate"
            :end-date="chartEndDate"
            :legacy-layout="useLegacySiloProfileVisualization"
            :hide-title="useLegacySiloProfileVisualization"
            @profileHover="updateProfileHover"
          />
        </section>
      </div>

      <div
        v-if="useLegacySiloProfileVisualization && profileHovered"
        class="profile-panel-table"
      >
        <SiloProfileInfoTable
          :sensorName="sensorName"
          :profileData="profileHovered"
          :sacaWeight="sacaWeight || 60"
          :foodByCompany="foodByCompany || []"
        />
      </div>
      <div
        v-else-if="!useLegacySiloProfileVisualization && transformedSiloData"
        class="profile-panel-table"
      >
        <SiloCard
          display="table"
          variant="detail"
          hide-header
          show-remaining-capacity
          :silo="transformedSiloData"
          :plate-height="plateHeight"
          :farm-pk="farmPk"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SiloProfileHistoryChart from "./SiloProfileHistoryChart.vue";
import SiloProfileVisualizationCanvas from "./SiloProfileVisualizationCanvas.vue";
import SiloProfileInfoTable from "./SiloProfileInfoTable.vue";
import SiloCard from "./SiloCard.vue";
import Loader from "./Loader.vue";
import { format, subDays } from 'date-fns';
import {
  filterStableSiloProfiles,
  getStabilityFilterThreshold,
} from '../utils/siloProfileStabilityFilter';
import {
  getPlateHeightFromSettings,
  transformSiloProfileForCard,
} from '../utils/transformSiloProfileForCard';
import { normalizeSiloLastProfile } from '../utils/siloLevelToKg';

export default {
  name: "SmartFeedProfileHistory3",
  props: [
    "sensor",
    "startDate",
    "endDate",
    "seekNewRange",
    "sacaWeight",
    "foodByCompany",
    "useLegacySiloProfileVisualization",
    "farmPk",
  ],
  components: {
    SiloProfileHistoryChart,
    SiloProfileVisualizationCanvas,
    SiloProfileInfoTable,
    SiloCard,
    Loader,
  },
  data() {
    return {
      loading: true,
      loadingChart: false,
      loadingCurves: false,
      startTime: null,
      endTime: null,
      selectedProfileId: null,
      hasError: false,
      errorMessage: '',
      siloSettings: [],
      chartProfiles: [],
      historyProfiles: [],
      lastProfile: null,
      profileHoverLocked: false,
      curvesByProfileId: {},
    };
  },
  watch: {
    async seekNewRange() {
      // Stale-while-revalidate: mantém os dados anteriores na tela
      // enquanto buscamos os novos. O banner em SmartfeedPage3 já comunica
      // que estamos atualizando — não exibimos loaders internos aqui para
      // que o usuário sempre tenha contexto.
      this.hasError = false;
      this.errorMessage = '';
      this.startTime = Date.now();
      await this.fetchData(this.startDate, this.endDate, { resetStream: true });
    },
    chartProfiles: {
      handler(newVal) {
        if (!newVal?.length || this.selectedProfileId != null) return;
        this.ensureSelectedProfile();
      },
      immediate: true,
    },
  },
  methods: {
    getSiloPk() {
      return this.sensor?.silo?.pk ?? this.sensor?.silo?.id ?? this.sensor?.silo ?? null;
    },
    getSensorPk() {
      return this.sensor?.sensor ?? this.sensor?.pk ?? this.sensor?.id ?? null;
    },
    sortProfilesByDatetime(profiles) {
      return [...(profiles || [])].sort((a, b) => {
        const ta = new Date(a?.datetime_fetched || 0).getTime();
        const tb = new Date(b?.datetime_fetched || 0).getTime();
        return ta - tb;
      });
    },
    isProfileDateInRange(profile, startDate, endDate) {
      if (!profile?.datetime_fetched || !startDate || !endDate) return false;
      const profileTime = new Date(profile.datetime_fetched).getTime();
      const startTime = new Date(startDate).getTime();
      const endTime = new Date(endDate).getTime();
      if (![profileTime, startTime, endTime].every(Number.isFinite)) return false;
      return profileTime >= startTime && profileTime <= endTime;
    },
    buildProfileFromSource(rawProfile) {
      if (!rawProfile || typeof rawProfile !== 'object') return null;
      const normalizedDate = rawProfile.datetime_fetched || rawProfile.last_updated || rawProfile.datetime || null;
      const normalizedLevel = rawProfile.level ?? rawProfile.last_hour_level ?? null;
      if (normalizedDate == null || normalizedLevel == null) return null;

      return {
        ...rawProfile,
        id: rawProfile.id ?? `home-${this.sensor?.silo ?? 'silo'}-${this.sensor?.sensor ?? 'sensor'}`,
        datetime_fetched: normalizedDate,
        level: Number(normalizedLevel),
      };
    },
    hydrateInitialProfileFromHomeCache(startDate, endDate) {
      // 1) Tenta usar o próprio sensor já pré-carregado na navegação da Home.
      const fromSensor = this.buildProfileFromSource(this.sensor);
      if (fromSensor && this.isProfileDateInRange(fromSensor, startDate, endDate)) {
        this.lastProfile = fromSensor;
        this.chartProfiles = [fromSensor];
        this.selectedProfileId = fromSensor.id;
        if (Array.isArray(fromSensor.curves) && fromSensor.curves.length > 0) {
          this.$set(this.curvesByProfileId, fromSensor.id, fromSensor.curves);
        }
        this.loading = false;
        return true;
      }

      // 2) Fallback: cache explícito da Home por barn+silo.
      const barnPk = this.$store.getters.getBarnTarget?.pk ?? this.$store.getters.getBarnTarget?.id ?? null;
      const siloPk = this.sensor?.silo ?? null;
      if (barnPk == null || siloPk == null) return false;
      const cachedHomeProfile = this.$store.getters.getCachedSiloDataHome(barnPk, siloPk, 30);
      const fromHomeCache = this.buildProfileFromSource(cachedHomeProfile);
      if (!fromHomeCache || !this.isProfileDateInRange(fromHomeCache, startDate, endDate)) return false;

      this.lastProfile = fromHomeCache;
      this.chartProfiles = [fromHomeCache];
      this.selectedProfileId = fromHomeCache.id;
      if (Array.isArray(fromHomeCache.curves) && fromHomeCache.curves.length > 0) {
        this.$set(this.curvesByProfileId, fromHomeCache.id, fromHomeCache.curves);
      }
      this.loading = false;
      return true;
    },
    findSiloDimensionsCurve(curves) {
      if (!Array.isArray(curves)) return null;
      return curves.find(
        c => c?.name === 'Silo dimensions' || c?.name === 'silo_dimensions'
      ) ?? null;
    },
    findCorrectedCurve(curves) {
      if (!Array.isArray(curves)) return null;
      return curves.find(
        c => c?.name === 'corrected_curve' || c?.name === 'Corrected curve'
      ) ?? null;
    },
    xySeries(profileCurve) {
      const xs = profileCurve?.points?.x;
      const ys = profileCurve?.points?.y;
      if (!Array.isArray(xs) || !Array.isArray(ys)) return [];
      return xs.map((x, idx) => ({ x, y: ys[idx] }));
    },
    getProfileWithCurves(profile) {
      if (!profile) return null;
      let curves = [...(this.curvesByProfileId[profile.id] ?? profile.curves ?? [])];
      const hasDimensions = curves.some(
        c => c?.name === 'Silo dimensions' || c?.name === 'silo_dimensions'
      );
      if (!hasDimensions && this.siloDimensionsCurve) {
        curves = [this.siloDimensionsCurve, ...curves];
      }
      return { ...profile, curves };
    },
    updateProfileHover(profile) {
      if (this.loadingCurves) return;
      if (profile?.id != null) {
        this.selectedProfileId = profile.id;
        this.profileHoverLocked = true;
      }
    },
    unwrapLastProfile(payload) {
      if (!payload) return null;
      if (Array.isArray(payload)) {
        const sensorPk = this.getSensorPk();
        return payload.find(profile => {
          const profileSensor = profile?.sensor ?? profile?.sensor_pk;
          return sensorPk != null && String(profileSensor) === String(sensorPk);
        }) || payload[0] || null;
      }
      if (Array.isArray(payload.results)) return this.unwrapLastProfile(payload.results);
      if (payload.detail && !payload.datetime_fetched && !payload.last_updated) return null;
      return payload;
    },
    mergeLastProfile(history, lastProfile, startDate, endDate) {
      const list = [...(history || [])];
      const profile = this.buildProfileFromSource(
        normalizeSiloLastProfile(this.unwrapLastProfile(lastProfile))
      );
      if (!profile) return list;

      const rangeStart = startDate || this.chartStartDate;
      const rangeEnd = endDate || this.chartEndDate;
      if (rangeStart && rangeEnd && !this.isProfileDateInRange(profile, rangeStart, rangeEnd)) {
        return list;
      }

      const lastTime = new Date(profile.datetime_fetched).getTime();
      const existingIndex = list.findIndex(item => {
        if (profile.id != null && item?.id != null && item.id === profile.id) return true;
        return Number.isFinite(lastTime) && new Date(item?.datetime_fetched).getTime() === lastTime;
      });

      if (existingIndex >= 0) {
        const existing = list[existingIndex];
        list[existingIndex] = {
          ...existing,
          ...profile,
          curves: profile.curves?.length ? profile.curves : existing.curves,
        };
      } else {
        list.push(profile);
      }
      return list;
    },
    syncChartProfiles(startDate, endDate) {
      const merged = this.mergeLastProfile(this.historyProfiles, this.lastProfile, startDate, endDate);
      if (!merged.length) return;
      this.chartProfiles = merged;
      this.hasError = false;
      this.loading = false;
      this.ensureSelectedProfile();
    },
    stableProfiles(profiles) {
      return filterStableSiloProfiles(
        profiles,
        getStabilityFilterThreshold(this.siloSettings)
      );
    },
    ensureSelectedProfile() {
      const profiles = this.chartProfiles || [];
      if (!profiles.length) return;
      const stable = this.stableProfiles(profiles);
      const visible = stable.length ? stable : profiles;
      const selectionStillVisible = this.profileHoverLocked
        && this.selectedProfileId != null
        && visible.some(profile => profile.id === this.selectedProfileId);
      if (selectionStillVisible) return;
      const latest = this.sortProfilesByDatetime(visible);
      this.selectedProfileId = latest[latest.length - 1]?.id ?? null;
    },
    processAndMapProfiles(data) {
      if (!data || !Array.isArray(data) || data.length === 0) {
        return [];
      }

      return this.stableProfiles(data).map(profile => ({
        y: profile.level,
        x: new Date(profile.datetime_fetched),
        id: profile.id,
        food: profile.food,
      }));
    },
    fetchSiloSettings: async function() {
      try {
        const siloSettingsData = await this.$store.dispatch('fetchSiloSettings', {
          silo_pk: this.sensor.silo
        });
        this.siloSettings = siloSettingsData;
      } catch (error) {
        console.error("Could not get silo settings.", error);
      }
    },
    fetchData: async function(startDate, endDate, { resetStream = false } = {}) {
      if (resetStream) {
        this.chartProfiles = [];
        this.historyProfiles = [];
        this.lastProfile = null;
        this.curvesByProfileId = {};
        this.selectedProfileId = null;
        this.profileHoverLocked = false;
      }

      this.loadingChart = true;
      this.loadingCurves = true;

      const chartPromise = this.fetchChartData(startDate, endDate).finally(() => {
        this.loadingChart = false;
      });
      const curvesPromise = this.fetchCurvesData(startDate, endDate).finally(() => {
        this.loadingCurves = false;
      });
      const lastProfilePromise = this.fetchLastProfile(startDate, endDate);

      await Promise.all([chartPromise, curvesPromise, lastProfilePromise]);
      this.loading = false;
      if (!this.chartProfiles.length) {
        this.hasError = true;
        this.errorMessage = 'Não foi possível carregar os dados deste silo';
      }
      this.endTime = Date.now();
    },
    async fetchChartData(startDate, endDate) {
      try {
        const result = await this.$store.dispatch('fetchSiloProfileHistory', {
          sensor_pk: this.sensor.sensor,
          start_date: startDate,
          end_date: endDate,
        });
        const list = Array.isArray(result) ? result : (result?.results ?? []);
        this.historyProfiles = list;
        this.syncChartProfiles(startDate, endDate);
      } catch (error) {
        console.error('Error fetching chart data:', error);
      }
    },
    async fetchCurvesData(startDate, endDate) {
      try {
        const curvesMap = await this.$store.dispatch('fetchSiloProfileCurvesMap', {
          sensor_pk: this.sensor.sensor,
          start_date: startDate,
          end_date: endDate,
        });
        this.curvesByProfileId = {
          ...this.curvesByProfileId,
          ...curvesMap,
        };
      } catch (error) {
        console.error('Error fetching curves:', error);
      }
    },
    fetchLastProfile: async function(startDate, endDate) {
      try {
        const data = await this.$store.dispatch('fetchSiloLastProfileBySilo', {
          silo_pk: this.sensor.silo,
        });
        const profile = this.buildProfileFromSource(
          normalizeSiloLastProfile(this.unwrapLastProfile(data))
        );
        if (!profile) return;

        this.lastProfile = profile;
        if (profile.curves?.length && profile.id != null) {
          this.$set(this.curvesByProfileId, profile.id, profile.curves);
        }
        this.syncChartProfiles(startDate, endDate);
        this.loading = false;
      } catch (error) {
        console.error("Error fetching last profile:", error);
      }
    },
  },
  computed: {
    sensorName() {
      return `${this.sensor?.silo_name || this.sensor?.name || 'Sensor'} (${this.sensor?.silo || "Sem silo"})`;
    },
    rawDataCount() {
      return this.chartProfiles.length;
    },
    loadingCurvesForCanvas() {
      return this.loadingCurves && !!this.firstProfile;
    },
    elapsedTime() {
      if (!this.startTime || !this.endTime) return 0;
      return ((this.endTime - this.startTime) / 1000).toFixed(2);
    },
    dateThreeDaysAgo() {
      return format(subDays(new Date(), 3), "yyyy-MM-dd'T'HH:mm:ss");
    },
    chartStartDate() {
      return this.startDate || this.dateThreeDaysAgo;
    },
    chartEndDate() {
      return this.endDate || format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
    },
    mappedProfiles() {
      const sorted = this.sortProfilesByDatetime(this.chartProfiles);
      return this.processAndMapProfiles(sorted);
    },
    firstProfile() {
      return this.chartProfiles[0] ? this.getProfileWithCurves(this.chartProfiles[0]) : null;
    },
    siloDimensionsCurve() {
      for (const profile of this.chartProfiles) {
        const curves = this.curvesByProfileId[profile.id] ?? profile.curves;
        const found = this.findSiloDimensionsCurve(curves);
        if (found) return found;
      }
      for (const curves of Object.values(this.curvesByProfileId)) {
        const found = this.findSiloDimensionsCurve(curves);
        if (found) return found;
      }
      return null;
    },
    profileHovered() {
      const profile = this.selectedProfileId != null
        ? this.chartProfiles.find(p => p.id === this.selectedProfileId)
        : this.chartProfiles[0];
      return profile ? this.getProfileWithCurves(profile) : null;
    },
    activeCanvasProfile() {
      return this.profileHovered || this.firstProfile;
    },
    siloSeries() {
      const curves = this.activeCanvasProfile?.curves;
      if (!Array.isArray(curves) || curves.length === 0) return [];
      const dimensions = this.findSiloDimensionsCurve(curves) || curves[0];
      return this.xySeries(dimensions);
    },
    profileSeries() {
      const curves = this.activeCanvasProfile?.curves;
      const corrected = this.findCorrectedCurve(curves);
      if (!corrected) return [];
      return this.xySeries(corrected);
    },
    useStraightSiloProfileVisualization() {
      return this.$store.getters.getCompanySettings?.settings?.client_frontend?.use_straight_silo_profile_visualization ?? false;
    },
    canvasFillLevel() {
      return Number(this.activeCanvasProfile?.level) || 0;
    },
    hasCanvasSeries() {
      if (!this.siloSeries.length) return false;
      if (this.useStraightSiloProfileVisualization) return true;
      return this.profileSeries.length > 0;
    },
    plateHeight() {
      return getPlateHeightFromSettings(this.siloSettings);
    },
    transformedSiloData() {
      return transformSiloProfileForCard(this.profileHovered, {
        sensorId: this.getSensorPk(),
        sacaWeight: this.sacaWeight || 60,
        foodByCompany: this.foodByCompany || [],
        siloNameFallback: this.sensor?.silo_name || this.sensor?.name,
        siloSettings: this.siloSettings,
        informedMaxWeight: this.sensor?.informed_max_weight,
      });
    },
  },
  async created() {
    this.startTime = Date.now();
    await this.fetchSiloSettings();
    const rangeStart = this.startDate || this.dateThreeDaysAgo;
    const rangeEnd = this.endDate;
    this.hydrateInitialProfileFromHomeCache(rangeStart, rangeEnd);
    await this.fetchData(rangeStart, rangeEnd);
  },
};
</script>

<style scoped>
.profile-history {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
}

.profile-history--legacy {
  max-width: 1000px;
}

.profile-history-loading {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

.loading-text {
  color: #78b843;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  animation: pulse 1.5s ease-in-out infinite;
  margin: 0;
}

.container-warnig {
  min-height: 240px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0;
}

.warning {
  max-width: 420px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.warning .text {
  background: #78b843;
  border-radius: 10px;
  color: #fff;
  padding: 20px;
  font-size: 15px;
  line-height: 1.45;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.profile-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.profile-panel--legacy {
  overflow: hidden;
}

.profile-panel-header {
  padding: 12px 16px 10px;
  border-bottom: 1px solid #e5e7eb;
  background: #fafafa;
  text-align: center;
}

.profile-panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.profile-panel-hint {
  margin: 4px 0 0;
  font-size: 11px;
  color: #6b7280;
}

.profile-panel-visual {
  display: grid;
  grid-template-columns: minmax(0, 0.78fr) minmax(0, 1.72fr);
  align-items: center;
  gap: 6px;
  padding: 10px 10px 12px 4px;
  border-bottom: 1px solid #e5e7eb;
}

.profile-panel-visual--legacy {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  gap: 12px;
  padding: 16px 12px 12px;
  overflow: hidden;
  min-height: 320px;
}

.profile-panel-silo {
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

.profile-panel-silo--legacy {
  flex: 0 0 34%;
  max-width: 380px;
  min-width: 220px;
  align-self: stretch;
  align-items: stretch;
  overflow: hidden;
}

.silo-canvas-responsive {
  width: 100%;
  height: 100%;
  min-height: 280px;
  max-height: 100%;
  display: block;
}

.silo-canvas-responsive >>> div {
  width: 100% !important;
  height: 100% !important;
  min-height: 0 !important;
  max-height: 100% !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.profile-panel-chart {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  height: 340px;
  padding: 0;
  overflow: hidden;
}

.profile-panel-chart--legacy {
  flex: 1 1 66%;
  min-width: 0;
  height: auto;
  min-height: 0;
  align-self: stretch;
}

.profile-panel-chart-title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: #6b7280;
  text-align: center;
  flex-shrink: 0;
}

.chart-responsive {
  width: 100%;
  height: 100%;
  min-width: 0;
  flex: 1 1 auto;
  min-height: 0;
}

.chart-responsive--legacy >>> .profile-history-chart-container {
  width: 100%;
  margin-left: 0;
}

.chart-responsive--legacy >>> .profile-history-chart {
  min-height: 0;
  height: 100%;
}

@media screen and (max-width: 900px) {
  .chart-responsive--legacy {
    height: 250px;
    flex: 0 0 auto;
  }
}

.profile-panel-table {
  display: flex;
  justify-content: center;
  padding: 12px 16px 16px;
}

.canvas-pulsing {
  animation: canvasPulse 2s ease-in-out infinite;
  pointer-events: none;
}

@keyframes canvasPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

@media screen and (max-width: 900px) {
  .profile-panel-visual--legacy {
    flex-direction: column;
    min-height: 0;
    padding: 12px 10px 0;
    gap: 8px;
    border-bottom: none;
  }

  .profile-panel-silo--legacy {
    flex: 0 0 auto;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    min-height: 0;
    height: 200px;
    max-height: 200px;
    overflow: hidden;
  }

  .profile-panel-chart--legacy {
    width: 100%;
    max-width: 100%;
    flex-shrink: 0;
    height: auto;
    min-height: 0;
    padding: 0 4px 8px;
    border-top: 1px solid #e5e7eb;
  }

  .profile-panel-chart-title {
    padding-top: 10px;
    font-size: 14px;
  }

  .silo-canvas-responsive,
  .silo-canvas-responsive >>> div {
    min-height: 0 !important;
    max-height: 200px !important;
  }
}

@media screen and (max-width: 640px) {
  .profile-panel-visual {
    grid-template-columns: 1fr;
  }

  .profile-panel-silo--legacy {
    height: 190px;
    max-height: 190px;
  }

  .profile-panel-chart--legacy {
    height: auto;
    min-height: 0;
  }

  .chart-responsive--legacy {
    height: 260px;
  }

  .silo-canvas-responsive,
  .silo-canvas-responsive >>> div {
    max-height: 190px !important;
  }

  .profile-panel-table {
    padding: 10px 8px 12px;
  }
}

@media screen and (max-width: 480px) {
  .profile-panel-title {
    font-size: 14px;
  }

  .profile-panel-chart--legacy {
    height: auto;
    min-height: 0;
  }

  .chart-responsive--legacy {
    height: 240px;
  }

  .profile-panel-silo--legacy {
    height: 180px;
    max-height: 180px;
  }

  .silo-canvas-responsive,
  .silo-canvas-responsive >>> div {
    max-height: 180px !important;
  }
}
</style>

